import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import { ShoppingCart, Boxes, AlertTriangle, ArrowRight, RefreshCw, IndianRupee, CheckCircle2, Mail, Users, Clock, Calendar, ClipboardList, ChevronRight, Truck, Zap, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import KpiCardsWidget from '../components/dashboard/KpiCardsWidget';
import SalesPipelineWidget from '../components/dashboard/SalesPipelineWidget';
import {
  GrievanceWidget, TipOfDayWidget, FeasibilityRisksWidget,
  PaymentRemindersWidget, CollectionCockpitWidget, StockDebtsWidget, CostVarianceWidget,
  ExpiryAlertsWidget,
} from '../components/dashboard/widgets';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canViewFinancials = isAdmin || isManager;
  const perms = user?.permissions || {};
  const canSee = (key) => isAdmin || perms[key] === true;
  // Per-widget visibility — admin can configure each user's dashboard from
  // the Users page. Owners/admins always see everything (closed-by-default
  // for everyone else, so a brand-new permission won't suddenly leak).
  const canSeeWidget = (widgetKey) => isAdmin || perms[widgetKey] === true;

  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [calendarOrders, setCalendarOrders] = useState([]);
  const [dispatchSummary, setDispatchSummary] = useState(null);
  const [inwardLot, setInwardLot] = useState(null);
  const [pendingIntel, setPendingIntel] = useState([]);
  const [pipelineRows, setPipelineRows] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState(null);
  const [delayLogs, setDelayLogs] = useState([]);
  const [paymentReminders, setPaymentReminders] = useState(null);
  const [collectionCockpit, setCollectionCockpit] = useState(null);
  const [stockDebts, setStockDebts] = useState(null);
  const [costVariance, setCostVariance] = useState(null);
  const [grievances, setGrievances] = useState([]);
  const [tipOfDay, setTipOfDay] = useState(null);
  const [feasibilityRisks, setFeasibilityRisks] = useState(null);
  const [productCodeAnalytics, setProductCodeAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alertSending, setAlertSending] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const navigate = useNavigate();

  const fetchAll = async () => {
    // Progressive loading — fire the FAST core queries first and flip the
    // page-level `loading` flag as soon as KPIs + alerts arrive. The
    // expensive AI / aggregate widgets continue fetching in the background
    // and pop in when ready. This collapses perceived load time from
    // ~slowest-of-20 (≥1.5s) to ~slowest-of-3 (≤400ms).
    const fast = Promise.all([
      axios.get(`${API}/dashboard/stats`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/dashboard/alerts`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/dashboard/todos`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    // Don't await — start the rest in parallel and let them finish on
    // their own. Each setState is independent.
    const rest = Promise.all([
      axios.get(`${API}/sales/calendar`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/dispatch/dashboard/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/tasks/overview`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/inward-lot/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/sales/pending-intelligence`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/sales/pipeline`, { params: { bucket: 'pending' }, withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/sales/pipeline`, { params: { bucket: 'planning' }, withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/sales/pipeline`, { params: { bucket: 'in_production' }, withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/inventory/expiry-alerts`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/sales/delay-logs`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/creditor-reminders/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/debtor-reminders/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/stock-debts/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/cost-variance/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/ai-memory`, { params: { status: 'active' }, withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/dashboard/tip-of-day`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/dashboard/feasibility-risks`, { params: { days: 7, limit: 3 }, withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/dashboard/product-code-analytics`, { params: { days: 90 }, withCredentials: true }).catch(() => ({ data: null })),
    ]);

    try {
      const [statsRes, alertsRes, todosRes] = await fast;
      setStats(statsRes.data);
      setAlerts(alertsRes.data || []);
      setTodos(todosRes.data || []);
      // Page is now visible — flip loading off so the user sees KPIs + alerts
      // immediately. The remaining widgets render their own placeholders
      // until their data arrives a few hundred ms later.
      setLoading(false);
      setRefreshing(false);

      const [calRes, dspRes, tasksRes, inwardLotRes, intelRes, pendPipe, planPipe, prodPipe, expiryRes, delayRes, payRemRes, collCockpitRes, stockDebtRes, cvRes, grvRes, tipRes, feasRes, pcAnalyticsRes] = await rest;
      setCalendarOrders((calRes.data || []).filter(o => o.days_left >= -1 && o.days_left <= 14));
      setDispatchSummary(dspRes.data);
      setInwardLot(inwardLotRes.data);
      setPendingIntel(intelRes.data || []);
      const prioRank = { high: 0, general: 1, low: 2 };
      const merged = [...(pendPipe.data || []), ...(planPipe.data || []), ...(prodPipe.data || [])].sort((a, b) => {
        if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
        const pa = prioRank[a.priority || 'general']; const pb = prioRank[b.priority || 'general'];
        if (pa !== pb) return pa - pb;
        const da = a.dispatch_due_date || '9999-12-31';
        const db2 = b.dispatch_due_date || '9999-12-31';
        return da.localeCompare(db2);
      });
      setPipelineRows(merged);
      setExpiryAlerts(expiryRes.data);
      setDelayLogs(delayRes.data || []);
      setPaymentReminders(payRemRes.data);
      setCollectionCockpit(collCockpitRes.data);
      setStockDebts(stockDebtRes.data);
      setCostVariance(cvRes.data);
      setGrievances(grvRes.data || []);
      setTipOfDay(tipRes.data || null);
      setFeasibilityRisks(feasRes.data || null);
      setProductCodeAnalytics(pcAnalyticsRes.data || null);
      void tasksRes;
    } catch (e) { console.error(e); setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Auto-refresh Inward+Lot counters every 30s so the Dashboard tiles stay live
  useEffect(() => {
    const id = setInterval(() => {
      axios.get(`${API}/inward-lot/summary`, { withCredentials: true })
        .then(r => setInwardLot(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);
  const refresh = () => { setRefreshing(true); fetchAll(); };

  const sendLowStockAlert = async () => {
    setAlertSending(true);
    try {
      const res = await axios.post(`${API}/invoices/alerts/low-stock`, {}, { withCredentials: true });
      setAlertSent(true);
      setTimeout(() => setAlertSent(false), 5000);
      if (res.data.message) toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send alert'); }
    finally { setAlertSending(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-zinc-400 animate-pulse text-sm">Loading dashboard...</div>
    </div>
  );

  const BATCH_STATUS_COLORS = {
    'Planned': 'bg-zinc-700 text-zinc-300',
    'In Production': 'bg-amber-900/50 text-amber-300',
    'QC': 'bg-blue-900/50 text-blue-300',
    'Completed': 'bg-green-900/50 text-green-300',
  };

  return (
    <div className="flex flex-col space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between order-first">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">
            {user?.name ? `Welcome, ${user.name.split(' ')[0]}` : 'Operations Dashboard'}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            {isAdmin ? 'Tirth Industries — Live Operations Overview' : `Your modules · ${user?.role || ''}`}
          </p>
        </div>
        <button data-testid="refresh-btn" onClick={refresh} disabled={refreshing} className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-xl text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 text-sm transition-all">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ═══════════════ TOP PRIORITIES ═══════════════ */}
      {/* 1. Grievances / Issue notices */}
      {canSeeWidget('widget_grievances') && grievances && grievances.length > 0 && (
        <div className="order-[-3]">
          <GrievanceWidget items={grievances.slice(0, 5)} total={grievances.length} onOpen={() => navigate('/ai-memory')} />
        </div>
      )}

      {/* Tip / lesson of the day */}
      {canSeeWidget('widget_tip_of_day') && <TipOfDayWidget tip={tipOfDay} />}

      {/* AI Feasibility blockers */}
      {canSeeWidget('widget_feasibility_risks') && feasibilityRisks && (feasibilityRisks.blocked_orders || 0) > 0 && (
        <div>
          <FeasibilityRisksWidget data={feasibilityRisks} onOpen={() => navigate('/sales')} onRefresh={() => {
            axios.get(`${API}/dashboard/feasibility-risks`, { params: { days: 7, limit: 3 }, withCredentials: true })
              .then(r => setFeasibilityRisks(r.data));
          }} />
        </div>
      )}

      {/* Product Code Usage Analytics */}
      {canSeeWidget('widget_product_code_analytics') && productCodeAnalytics && productCodeAnalytics.registered_codes > 0 && (
        <ProductCodeAnalytics data={productCodeAnalytics} onOpen={() => navigate('/inventory')} />
      )}

      {/* Cash Balance Bar */}
      {canSeeWidget('widget_cash_balance') && canViewFinancials && stats && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IndianRupee size={18} className="text-amber-400" />
            <span className="text-zinc-400 text-sm">Current Cash Balance</span>
          </div>
          <span className={`font-mono font-semibold text-lg ${stats.cash_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {fmt(stats.cash_balance)}
          </span>
          {stats.cash_balance < 0 && <span className="ml-2 text-xs bg-red-900/30 text-red-300 px-2 py-1 rounded-lg">Low Cash Alert</span>}
        </div>
      )}

      {/* Collection Cockpit (debtor receivables) — placed ABOVE vendor
          payments. iter201: visible to admin/owner by default; toggleable
          per-user via widget_collection_cockpit. */}
      {canSeeWidget('widget_collection_cockpit') && canViewFinancials && (canSee('debtors') || isAdmin) && collectionCockpit && (collectionCockpit.overdue.count + collectionCockpit.pay_today.count + collectionCockpit.upcoming_7d.count > 0) && (
        <div className="order-[-2]">
          <CollectionCockpitWidget data={collectionCockpit} onOpen={() => navigate('/debtor-reminders')} />
        </div>
      )}

      {/* Payment Reminders */}
      {canSeeWidget('widget_payment_reminders') && canViewFinancials && (canSee('creditors') || isAdmin) && paymentReminders && (paymentReminders.overdue.count + paymentReminders.pay_today.count + paymentReminders.upcoming_7d.count > 0) && (
        <div className="order-[-1]">
          <PaymentRemindersWidget data={paymentReminders} onOpen={() => navigate('/creditor-reminders')} />
        </div>
      )}

      {/* Stock Debts */}
      {canSeeWidget('widget_stock_debts') && stockDebts && stockDebts.total_debts > 0 && (
        <StockDebtsWidget data={stockDebts} onOpen={() => navigate('/stock-debts')} />
      )}

      {/* Cost Variance */}
      {canSeeWidget('widget_cost_variance') && isAdmin && costVariance && (costVariance.kpi?.total_flags || 0) > 0 && (
        <CostVarianceWidget data={costVariance} onOpen={() => navigate('/cost-variance')} />
      )}

      {/* Expiry Alerts */}
      {canSeeWidget('widget_expiry_alerts') && (canSee('inventory') || isAdmin) && expiryAlerts && (expiryAlerts.counts.expired + expiryAlerts.counts.within_30 + expiryAlerts.counts.within_60 + expiryAlerts.counts.within_90 > 0) && (
        <ExpiryAlertsWidget data={expiryAlerts} onPrioritize={async (lotId) => {
          try {
            await axios.post(`${API}/inventory/lots/${lotId}/prioritize-fifo`, {}, { withCredentials: true });
            toast.success('Marked for FIFO priority — will be used first in next production run');
            fetchAll();
          } catch { toast.error('Failed'); }
        }} />
      )}

      {/* Delay Tracking */}
      {canSeeWidget('widget_delay_tracking') && canSee('sales') && delayLogs.length > 0 && (
        <DelayTracking logs={delayLogs} onOpen={() => navigate('/sales')} />
      )}

      {/* Sales Pipeline */}
      {canSeeWidget('widget_sales_pipeline') && canSee('sales') && (
        <SalesPipelineWidget rows={pipelineRows} onOpenFull={() => navigate('/sales')} />
      )}

      {/* KPI Cards */}
      {canSeeWidget('widget_kpi_cards') && <KpiCardsWidget stats={stats} user={user} />}

      {/* Sales Entity Breakdown */}
      {canSeeWidget('widget_sales_entity_breakdown') && canViewFinancials && stats && (
        <SalesEntityBreakdown stats={stats} onOpenRevenueAnalytics={() => navigate('/analytics/revenue')} />
      )}

      {/* Pending Orders Intelligence */}
      {canSeeWidget('widget_pending_intel') && canSee('sales') && pendingIntel.length > 0 && (
        <PendingIntelWidget orders={pendingIntel} onOpen={() => navigate('/sales')} />
      )}

      {/* Upcoming Dispatches + Priority To-Dos */}
      {(canSeeWidget('widget_upcoming_dispatches') || canSeeWidget('widget_priority_todos')) && (canSee('sales') || canSee('dispatch') || canSee('tasks')) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {canSeeWidget('widget_upcoming_dispatches') && (canSee('sales') || canSee('dispatch')) && (
            <UpcomingDispatches orders={calendarOrders} onOpen={() => navigate('/po-calendar')} />
          )}
          {canSeeWidget('widget_priority_todos') && canSee('tasks') && (
            <PriorityTodos todos={todos} onOpen={() => navigate('/tasks')} />
          )}
        </div>
      )}

      {/* Dispatch Status Widget */}
      {canSeeWidget('widget_dispatch_status') && dispatchSummary && canSee('dispatch') && (
        <DispatchStatusWidget summary={dispatchSummary} onOpen={() => navigate('/dispatch-calendar')} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        {canSeeWidget('widget_revenue_chart') && canSee('sales') && canViewFinancials && (
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4">Revenue Trend — Last 12 Months</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats?.revenue_chart || []} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#71717A', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} width={42} />
                <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} formatter={(v) => [fmt(v), 'Revenue']} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
                <Bar dataKey="revenue" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Alerts */}
        {canSeeWidget('widget_alerts') && (
          <div className={`${canSeeWidget('widget_revenue_chart') && canSee('sales') && canViewFinancials ? '' : 'lg:col-span-3'} bg-zinc-900 border border-zinc-800 rounded-xl p-5`}>
            <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4 flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-400" />
              Alerts ({alerts.length})
            </h3>
            <div className="space-y-2.5 max-h-52 overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="flex items-center gap-2 text-green-400 text-xs">
                  <CheckCircle2 size={14} /> All systems normal
                </div>
              ) : alerts.map((a, i) => (
                <div key={i} className={`p-3 rounded-lg text-xs border ${a.type === 'critical' || a.type === 'warning' ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-amber-900/20 border-amber-800 text-amber-300'}`}>
                  <div className="font-medium">{a.module}</div>
                  <div className="text-zinc-400 mt-0.5">{a.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {(canSeeWidget('widget_top_customers') || canSeeWidget('widget_recent_batches')) && (canSee('sales') || canSee('production')) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {canSeeWidget('widget_top_customers') && canSee('sales') && canViewFinancials && (
            <TopCustomers stats={stats} />
          )}
          {canSeeWidget('widget_recent_batches') && canSee('production') && (
            <RecentBatches batches={stats?.recent_batches || []} batchColors={BATCH_STATUS_COLORS} />
          )}
        </div>
      )}

      {canSeeWidget('widget_low_stock') && canSee('inventory') && (
        <LowStockWidget items={stats?.low_stock_items || []} alertSending={alertSending} alertSent={alertSent} onSendAlert={sendLowStockAlert} />
      )}
    </div>
  );
}

/* ─── Small inline helpers (kept here; each <100 lines) ──────────────── */

function ProductCodeAnalytics({ data, onOpen }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="product-code-analytics-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-600/30 font-mono text-[10px] font-bold">CODE</span>
            Product Code Usage — last {data.window_days} days
          </h3>
          <p className="text-zinc-500 text-[11px] mt-0.5">
            {data.active_codes} of {data.registered_codes} codes active · {data.stale?.length || 0} stale
          </p>
        </div>
        <button onClick={onOpen} className="text-[10px] text-amber-400 hover:underline">Manage codes →</button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <PcColumn title="Top by Batches" rows={(data.top_by_batches || []).filter(r => r.batch_count > 0)} emptyMsg="No batches this window"
          render={r => <div className="text-amber-400 font-mono text-[10px] font-semibold whitespace-nowrap">{r.batch_count}×</div>}
          testPrefix="pc-top-batches" />
        <PcColumn title="Top by Revenue" rows={(data.top_by_revenue || []).filter(r => r.revenue > 0)} emptyMsg="No sales this window"
          render={r => <div className="text-green-400 font-mono text-[10px] font-semibold whitespace-nowrap">₹{Math.round(r.revenue).toLocaleString('en-IN')}</div>}
          testPrefix="pc-top-revenue" />
        <div>
          <h4 className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider mb-2">Stale Codes <span className="text-zinc-600 normal-case">(no activity)</span></h4>
          {data.stale?.length === 0 && <div className="text-green-400 text-xs italic">All codes active ✓</div>}
          <div className="space-y-1.5">
            {(data.stale || []).slice(0, 5).map(r => (
              <div key={r.code} className="flex items-center justify-between bg-zinc-950 rounded px-2 py-1.5" data-testid={`pc-stale-${r.code}`}>
                <span className="font-mono text-zinc-400 text-[10px] truncate">{r.code}</span>
                <span className="text-zinc-600 text-[10px] truncate max-w-[100px]">{r.real_name || ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PcColumn({ title, rows, emptyMsg, render, testPrefix }) {
  return (
    <div>
      <h4 className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider mb-2">{title}</h4>
      {rows.length === 0 && <div className="text-zinc-600 text-xs italic">{emptyMsg}</div>}
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.code} className="flex items-center justify-between bg-zinc-950 rounded px-2 py-1.5" data-testid={`${testPrefix}-${r.code}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-mono text-sky-300 text-[10px] font-bold truncate">{r.code}</span>
              <span className="text-zinc-500 text-[10px] truncate">{r.real_name || ''}</span>
            </div>
            {render(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

function DelayTracking({ logs, onOpen }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="delay-tracking-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Clock size={14} className="text-amber-400" /> Delay Tracking
          </h3>
          <p className="text-zinc-500 text-[11px] mt-0.5">{logs.length} delay {logs.length === 1 ? 'record' : 'records'} logged · most recent first</p>
        </div>
        <button onClick={onOpen} data-testid="open-pipeline-from-delays"
          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-0.5">
          Open Pipeline <ChevronRight size={12} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="delay-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-950/50">
              {['When', 'Order', 'Client', 'Reason', 'New Dispatch', 'Logged By'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.slice(0, 8).map(d => (
              <tr key={d.id} className="border-b border-zinc-800/50" data-testid={`delay-row-${d.id}`}>
                <td className="px-3 py-2 text-zinc-400 font-mono text-[10px]">{(d.logged_at || d.at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="px-3 py-2 text-amber-300 font-mono text-[11px] font-semibold">{d.voucher_no || d.order_id?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-zinc-100 text-xs">{d.client || '—'}</td>
                <td className="px-3 py-2 text-zinc-300 text-xs max-w-[280px] truncate" title={d.reason}>{d.reason || '—'}</td>
                <td className="px-3 py-2 text-red-300 font-mono text-[11px]">{d.delayed_to || '—'}</td>
                <td className="px-3 py-2 text-zinc-500 text-[11px]">{d.logged_by || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesEntityBreakdown({ stats, onOpenRevenueAnalytics }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="gst-breakdown">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
          <IndianRupee size={14} className="text-amber-400" />
          Sales Entity Breakdown
        </h3>
        <button onClick={onOpenRevenueAnalytics} className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1">
          Detailed analytics <ArrowRight size={11} />
        </button>
      </div>
      <div className="text-[10px] uppercase text-zinc-500 mb-1.5 font-semibold tracking-wider">Booked Sales (All Orders)</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <button onClick={onOpenRevenueAnalytics} data-testid="booked-gst"
          className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-left hover:bg-amber-500/15 transition-colors">
          <div className="text-amber-400 text-xs font-medium mb-1">Tirth Industries (GST)</div>
          <div className="text-zinc-50 font-mono font-bold text-lg">{fmt(stats.booked_gst || 0)}</div>
          <div className="text-zinc-500 text-xs mt-1">
            {stats.booked_revenue > 0 ? `${((stats.booked_gst / stats.booked_revenue) * 100).toFixed(1)}% of booked` : '—'}
          </div>
        </button>
        <button onClick={onOpenRevenueAnalytics} data-testid="booked-non-gst"
          className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 text-left hover:bg-zinc-700/70 transition-colors">
          <div className="text-zinc-300 text-xs font-medium mb-1">Tirth Enterprises (Non-GST)</div>
          <div className="text-zinc-50 font-mono font-bold text-lg">{fmt(stats.booked_non_gst || 0)}</div>
          <div className="text-zinc-500 text-xs mt-1">
            {stats.booked_revenue > 0 ? `${((stats.booked_non_gst / stats.booked_revenue) * 100).toFixed(1)}% of booked` : '—'}
          </div>
        </button>
      </div>
      <div className="text-[10px] uppercase text-zinc-500 mb-1.5 font-semibold tracking-wider">Dispatched (Realised)</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-2.5">
          <div className="text-green-400 text-[11px]" data-testid="gst-revenue">GST · {fmt(stats.gst_revenue || 0)}</div>
        </div>
        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-2.5">
          <div className="text-green-400 text-[11px]" data-testid="non-gst-revenue">Non-GST · {fmt(stats.non_gst_revenue || 0)}</div>
        </div>
      </div>
    </div>
  );
}

function PendingIntelWidget({ orders, onOpen }) {
  const stateCounts = { in_stock: 0, partial_stock: 0, in_production: 0, in_planning: 0, need_production: 0 };
  let overdueCount = 0;
  let readyOrders = 0;
  orders.forEach(o => {
    if (o.is_overdue) overdueCount++;
    if (o.can_fully_dispatch) readyOrders++;
    (o.items || []).forEach(it => { if (stateCounts[it.state] !== undefined) stateCounts[it.state]++; });
  });
  const tiles = [
    { key: 'in_stock',        label: 'Ready to Dispatch', value: stateCounts.in_stock,        color: 'text-green-300',  bg: 'bg-green-500/10 border-green-500/25' },
    { key: 'partial_stock',   label: 'Partial Stock',     value: stateCounts.partial_stock,   color: 'text-amber-300',  bg: 'bg-amber-500/10 border-amber-500/25' },
    { key: 'in_production',   label: 'In Production',     value: stateCounts.in_production,   color: 'text-blue-300',   bg: 'bg-blue-500/10 border-blue-500/25' },
    { key: 'in_planning',     label: 'In Planning',       value: stateCounts.in_planning,     color: 'text-purple-300', bg: 'bg-purple-500/10 border-purple-500/25' },
    { key: 'need_production', label: 'Need Production',   value: stateCounts.need_production, color: 'text-red-300',    bg: 'bg-red-500/10 border-red-500/25' },
  ];
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="pending-intel-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Zap size={14} className="text-amber-400" />
            Pending Orders — Dispatch Intelligence
          </h3>
          <div className="text-zinc-500 text-[11px] mt-0.5">
            {orders.length} pending {orders.length === 1 ? 'order' : 'orders'} · <span className="text-green-300">{readyOrders} fully dispatchable</span>
            {overdueCount > 0 && <> · <span className="text-red-300">{overdueCount} overdue</span></>}
          </div>
        </div>
        <button onClick={onOpen} data-testid="open-pipeline-btn"
          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-0.5">
          Open Pipeline <ChevronRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {tiles.map(t => (
          <button key={t.key} onClick={onOpen} data-testid={`intel-tile-${t.key}`}
            className={`p-3 rounded-xl border ${t.bg} hover:opacity-90 transition-all text-left`}>
            <div className="text-zinc-400 text-[10px] uppercase tracking-wider">{t.label}</div>
            <div className={`font-mono font-bold text-2xl mt-1 ${t.color}`}>{t.value}</div>
            <div className="text-zinc-600 text-[10px] mt-0.5">line items</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function UpcomingDispatches({ orders, onOpen }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="upcoming-dispatches">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
          <Calendar size={14} className="text-amber-400" />
          Upcoming Dispatches
        </h3>
        <button onClick={onOpen} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-0.5 transition-colors">
          Full Calendar <ChevronRight size={12} />
        </button>
      </div>
      <div className="space-y-2 max-h-52 overflow-y-auto">
        {orders.length === 0 ? (
          <div className="text-zinc-500 text-xs flex items-center gap-2"><CheckCircle2 size={13} className="text-green-400" /> No dispatches due in the next 14 days</div>
        ) : orders.map(o => {
          const urgency = o.urgency === 'overdue' ? 'bg-red-900/30 border-red-800' : o.urgency === 'due_soon' ? 'bg-amber-900/20 border-amber-800' : 'bg-zinc-800/40 border-zinc-700';
          const dayLabel = o.days_left < 0 ? `${Math.abs(o.days_left)}d overdue` : o.days_left === 0 ? 'Today' : o.days_left === 1 ? 'Tomorrow' : `${o.days_left}d`;
          return (
            <div key={o.id} className={`flex items-center justify-between p-2.5 rounded-xl border text-xs ${urgency}`}>
              <div>
                <div className="text-zinc-200 font-medium">{o.client}</div>
                <div className="text-zinc-500">{o.product_name} · {o.quantity} {o.unit || 'kg'}</div>
              </div>
              <div className="text-right">
                <div className={`font-semibold ${o.days_left < 0 ? 'text-red-400' : o.days_left <= 3 ? 'text-amber-400' : 'text-zinc-300'}`}>{dayLabel}</div>
                <div className="text-zinc-500">{o.due_date_str}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriorityTodos({ todos, onOpen }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="priority-todos">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
          <ClipboardList size={14} className="text-amber-400" />
          Priority To-Dos
        </h3>
        <button onClick={onOpen} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-0.5 transition-colors">
          All Tasks <ChevronRight size={12} />
        </button>
      </div>
      <div className="space-y-2 max-h-52 overflow-y-auto">
        {todos.length === 0 ? (
          <div className="text-zinc-500 text-xs flex items-center gap-2"><CheckCircle2 size={13} className="text-green-400" /> No high-priority tasks pending</div>
        ) : todos.map(task => {
          const isOverdue = task.due_date && new Date(task.due_date) < new Date();
          return (
            <div key={task.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-zinc-800/40 border border-zinc-700/50 text-xs">
              <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-zinc-200 font-medium truncate">{task.title}</div>
                <div className="text-zinc-500">{task.module} · {task.assigned_to || 'Unassigned'}</div>
              </div>
              {task.due_date && (
                <div className={`text-xs font-mono flex-shrink-0 ${isOverdue ? 'text-red-400' : 'text-zinc-400'}`}>
                  {task.due_date?.slice(0, 10)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DispatchStatusWidget({ summary, onOpen }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="dispatch-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
          <Truck size={14} className="text-green-400" /> Dispatch Status
        </h3>
        <button onClick={onOpen} className="text-xs text-green-400 hover:text-green-300 flex items-center gap-0.5">All Dispatches <ChevronRight size={12} /></button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-zinc-800/50 rounded-xl p-3 text-center cursor-pointer hover:bg-zinc-800" onClick={onOpen}>
          <div className="text-xl font-bold text-zinc-300">{summary.planned?.count || 0}</div>
          <div className="text-xs text-zinc-500">Planned</div>
        </div>
        <div className="bg-amber-900/20 rounded-xl p-3 text-center cursor-pointer hover:bg-amber-900/30" onClick={onOpen}>
          <div className="text-xl font-bold text-amber-300">{summary.in_transit?.count || 0}</div>
          <div className="text-xs text-amber-400/60">In Transit</div>
        </div>
        <div className="bg-green-900/20 rounded-xl p-3 text-center cursor-pointer hover:bg-green-900/30" onClick={onOpen}>
          <div className="text-xl font-bold text-green-300">{summary.received?.count || 0}</div>
          <div className="text-xs text-green-400/60">Received</div>
        </div>
      </div>
      {(summary.in_transit?.items || []).length > 0 && (
        <div className="space-y-1.5">
          {summary.in_transit.items.slice(0, 3).map(d => (
            <div key={d.id} className="flex items-center justify-between p-2 bg-amber-900/10 rounded-lg border border-amber-800/30 text-xs cursor-pointer" onClick={onOpen}>
              <div><span className="text-amber-300 font-mono font-bold mr-2">{d.id}</span><span className="text-zinc-300">{d.client}</span></div>
              <span className="text-amber-400 font-mono">{d.total_weight} kg</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopCustomers({ stats }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="top-customers">
      <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4 flex items-center gap-2">
        <Users size={14} className="text-amber-400" />
        Top 5 Customers by Revenue
      </h3>
      <div className="space-y-3">
        {(stats?.top_customers || []).length === 0 ? (
          <div className="text-zinc-500 text-xs">No dispatched orders yet</div>
        ) : (stats.top_customers || []).filter(c => c.name).map((c, i) => {
          const maxRev = stats.top_customers.filter(x => x.name)[0]?.revenue || 1;
          const pct = Math.round((c.revenue / maxRev) * 100);
          return (
            <div key={c.name} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-4">{i + 1}.</span>
                  <span className="text-zinc-200 font-medium truncate max-w-[160px]">{c.name}</span>
                  <span className="text-zinc-600">{c.orders} order{c.orders !== 1 ? 's' : ''}</span>
                </div>
                <span className="font-mono text-amber-400 font-semibold">{fmt(c.revenue)}</span>
              </div>
              <div className="bg-zinc-800 rounded-full h-1.5 w-full">
                <div className="bg-amber-500 rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecentBatches({ batches, batchColors }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
      <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4">Recent Production Batches</h3>
      <div className="space-y-2">
        {batches.map(b => (
          <div key={b.id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
            <div>
              <span className="font-mono text-amber-400 text-xs font-semibold">{b.id}</span>
              <div className="text-zinc-300 text-xs mt-0.5">{b.product_name}</div>
            </div>
            <div className="text-right">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${batchColors[b.status] || 'bg-zinc-700 text-zinc-300'}`}>{b.status}</span>
              <div className="text-zinc-500 text-xs mt-0.5">{b.quantity} {b.unit || 'kg'}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LowStockWidget({ items, alertSending, alertSent, onSendAlert }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Boxes size={14} className="text-amber-400" />
            Low Stock Items
          </h3>
          {items.length > 0 && (
            <button data-testid="send-low-stock-alert-btn" onClick={onSendAlert} disabled={alertSending}
              className="flex items-center gap-1.5 text-xs bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50">
              {alertSending ? <RefreshCw size={11} className="animate-spin" /> : <Mail size={11} />}
              {alertSent ? 'Alert Sent!' : 'Send Alert'}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="text-green-400 text-xs flex items-center gap-2"><CheckCircle2 size={14} /> All stock levels healthy</div>
          ) : items.map(m => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
              <div className="text-zinc-300 text-xs font-medium">{m.name}</div>
              <div className="text-right">
                <div className={`text-xs font-mono font-semibold ${m.current_stock === 0 ? 'text-red-400' : 'text-yellow-400'}`}>{m.current_stock} {m.unit}</div>
                <div className="text-zinc-500 text-xs">Reorder: {m.reorder_level}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
